// src/pages/navbar/Home.jsx
import { Link } from 'react-router-dom';
import {
  Recycle,
  ArrowRight,
  Briefcase,
  Target,
  Truck,
  MessageSquare,
  Bell,
  BarChart3,
  ShieldCheck,
  Building2,
  UserCircle,
  CheckCircle,
  MapPin,
  Zap,
} from 'lucide-react';
import { motion } from 'framer-motion';

const ROLES = [
  {
    icon: UserCircle,
    title: 'Volunteers',
    color: 'emerald',
    desc: 'Browse open opportunities, get AI-powered matches based on your skills, apply in one click, and track every application from your dashboard.',
    links: [
      { label: 'Browse Opportunities', to: '/opportunities' },
      { label: 'Volunteer Login', to: '/login' },
      { label: 'Sign Up Free', to: '/register' },
    ],
  },
  {
    icon: Building2,
    title: 'NGOs',
    color: 'teal',
    desc: 'Post volunteer opportunities, review incoming applications, manage pickup requests, and communicate with your team - all in one place.',
    links: [
      { label: 'Browse Opportunities', to: '/opportunities' },
      { label: 'Post an Opportunity', to: '/register' },
      { label: 'Sign In', to: '/login' },
    ],
  },
  {
    icon: Truck,
    title: 'Collection Agents',
    color: 'sky',
    desc: 'View assigned waste-pickup jobs, track locations, update statuses in real time, and stay connected with NGOs through the platform.',
    links: [
      { label: 'Explore Opportunities', to: '/opportunities' },
      { label: 'Agent Login', to: '/login' },
    ],
  },
];

const FEATURES = [
  {
    icon: Target,
    title: 'AI Match Suggestions',
    desc: 'Smart algorithm scores your skills and availability against open NGO opportunities and surfaces the best fits automatically.',
  },
  {
    icon: Briefcase,
    title: 'Opportunity Board',
    desc: 'Browse, filter by location or waste type, and apply to verified opportunities posted by NGOs across the city.',
  },
  {
    icon: Truck,
    title: 'Pickup Scheduling',
    desc: 'NGOs request waste pickups, agents are assigned automatically, and all parties get real-time status updates.',
  },
  {
    icon: MessageSquare,
    title: 'Real-time Chat',
    desc: 'Every role has a dedicated inbox. Volunteers, NGOs, and agents can message each other directly without leaving the platform.',
  },
  {
    icon: Bell,
    title: 'Smart Notifications',
    desc: 'Application decisions, new matches, pickup assignments, and admin alerts delivered instantly - with unread counts in the sidebar.',
  },
  {
    icon: BarChart3,
    title: 'Analytics & Reports',
    desc: 'Admins and NGOs can track volunteer engagement, pickup completion rates, application trends, and export data as needed.',
  },
  {
    icon: ShieldCheck,
    title: 'Role-based Access',
    desc: 'Volunteer, NGO, Agent, and Admin - each role sees only what they need with protected routes and granular permissions.',
  },
  {
    icon: MapPin,
    title: 'Location-aware',
    desc: 'Geolocation-aware opportunity filtering and distance-based sorting connect volunteers to nearby cleanup events.',
  },
];

const STEPS = [
  {
    step: '01',
    title: 'Create your account',
    desc: 'Sign up as a volunteer, NGO, or collection agent. Your personalized dashboard is ready immediately.',
  },
  {
    step: '02',
    title: 'Get matched instantly',
    desc: 'Our AI reads your profile and surfaces the best-fit opportunities - or post your own as an NGO.',
  },
  {
    step: '03',
    title: 'Take action',
    desc: 'Apply, schedule pickups, chat, and track everything from a single clean dashboard.',
  },
];

const colorMap = {
  emerald: {
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    icon: 'bg-emerald-100 text-emerald-600',
    link: 'text-emerald-700 hover:text-emerald-900',
  },
  teal: {
    bg: 'bg-teal-50',
    border: 'border-teal-200',
    icon: 'bg-teal-100 text-teal-600',
    link: 'text-teal-700 hover:text-teal-900',
  },
  sky: {
    bg: 'bg-sky-50',
    border: 'border-sky-200',
    icon: 'bg-sky-100 text-sky-600',
    link: 'text-sky-700 hover:text-sky-900',
  },
};

const IMPACT_STATS = [
  { label: 'Active Opportunities', value: '8+' },
  { label: 'Role-specific Workflows', value: '4' },
  { label: 'Realtime Collaboration', value: '24/7' },
  { label: 'Volunteer Matching Speed', value: '< 10s' },
];

const sectionVariant = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: 'easeOut' },
  },
};

const cardVariant = {
  hidden: { opacity: 0, y: 18 },
  show: (index = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: index * 0.06, duration: 0.4, ease: 'easeOut' },
  }),
};

export default function Home() {
  return (
    <div className="min-h-screen bg-slate-50">

      <section className="relative flex min-h-[calc(100vh-4rem)] items-center overflow-hidden bg-slate-950 px-5 py-10 sm:min-h-[calc(100vh-4.5rem)] sm:px-8 sm:py-12 lg:px-12">
        <div className="pointer-events-none absolute -top-24 left-1/2 h-[540px] w-[540px] -translate-x-1/2 rounded-full bg-emerald-500/15 blur-3xl" />
        <div className="pointer-events-none absolute -right-20 top-16 h-[360px] w-[360px] rounded-full bg-teal-400/12 blur-3xl" />
        <div className="pointer-events-none absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-cyan-400/10 blur-3xl" />

        <div className="relative mx-auto grid max-w-6xl gap-8 lg:grid-cols-[1.08fr_0.92fr] lg:items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-4 py-1.5 text-sm font-medium text-emerald-300">
              <Zap size={13} />
              Smart coordination for civic impact
            </div>

            <h1 className="mb-5 text-4xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl xl:text-6xl">
              Run cleaner city operations with{' '}
              <span className="bg-linear-to-r from-emerald-300 to-teal-300 bg-clip-text text-transparent">
                one intelligent platform
              </span>
            </h1>

            <p className="mb-7 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
              WasteZero connects volunteers, NGOs, and collection agents through AI matching, realtime communication,
              and operational dashboards that keep every action visible and accountable.
            </p>

            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <Link
                to="/register"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-8 py-3.5 text-base font-semibold text-white transition-all duration-200 hover:bg-emerald-700 active:scale-[0.98]"
              >
                Start Free
                <ArrowRight size={17} />
              </Link>
              <Link
                to="/opportunities"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-100 bg-white px-8 py-3.5 text-base font-semibold text-slate-900 shadow-md shadow-slate-950/20 transition-all duration-200 hover:bg-emerald-50 hover:text-emerald-800"
              >
                <Briefcase size={16} className="text-emerald-700" />
                Explore Opportunities
              </Link>
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-slate-400">
              {['Volunteers', 'NGOs', 'Collection Agents', 'Admins'].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5">
                  <CheckCircle size={13} className="text-emerald-400" />
                  {t}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.08, ease: 'easeOut' }}
            className="rounded-3xl border border-white/10 bg-white/4 p-4 shadow-2xl backdrop-blur lg:p-5"
          >
            <div className="rounded-2xl border border-emerald-300/20 bg-slate-900/85 p-4 lg:p-5">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-200">Live Operations Feed</p>
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2.5 py-1 text-[11px] font-semibold text-emerald-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  Realtime
                </span>
              </div>

              <div className="space-y-2.5 text-xs text-slate-300">
                {[
                  '3 new volunteer applications received',
                  'Pickup route assigned to agent team',
                  'Opportunity match score updated by AI',
                ].map((item) => (
                  <div key={item} className="rounded-xl border border-slate-800 bg-slate-900 px-3 py-2">
                    {item}
                  </div>
                ))}
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2.5">
                {IMPACT_STATS.slice(0, 2).map((stat) => (
                  <div key={stat.label} className="rounded-xl border border-slate-800 bg-slate-900 px-3 py-2.5">
                    <p className="text-lg font-bold text-white">{stat.value}</p>
                    <p className="mt-1 text-[11px] text-slate-400">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <motion.section
        variants={sectionVariant}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        className="px-5 py-14 sm:px-8 lg:px-12"
      >
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-4 md:grid-cols-4">
          {IMPACT_STATS.map((stat, idx) => (
            <motion.div
              key={stat.label}
              custom={idx}
              variants={cardVariant}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.35 }}
              className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
            >
              <p className="text-2xl font-extrabold text-slate-900">{stat.value}</p>
              <p className="mt-1 text-xs font-medium text-slate-500">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </motion.section>

      <motion.section
        variants={sectionVariant}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        className="px-5 py-16 sm:px-8 lg:px-12"
      >
        <div className="mx-auto max-w-6xl">
          <div className="mb-10 text-center">
            <p className="mb-2 text-sm font-semibold uppercase tracking-widest text-emerald-600">Built for every role</p>
            <h2 className="text-3xl font-bold text-slate-900 sm:text-4xl">Purpose-built workspaces for each team</h2>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {ROLES.map((role, idx) => {
              const c = colorMap[role.color];
              return (
                <motion.div
                  key={role.title}
                  custom={idx}
                  variants={cardVariant}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, amount: 0.25 }}
                  className={`flex flex-col rounded-2xl border ${c.border} ${c.bg} p-7 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md`}
                >
                  <div className={`mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl ${c.icon}`}>
                    <role.icon size={22} />
                  </div>
                  <h3 className="mb-2 text-lg font-bold text-slate-900">{role.title}</h3>
                  <p className="mb-5 grow text-sm leading-relaxed text-slate-600">{role.desc}</p>
                  <div className="flex flex-wrap gap-3">
                    {role.links.map((l) => (
                      <Link
                        key={l.label}
                        to={l.to}
                        className={`inline-flex items-center gap-1 text-sm font-semibold ${c.link} transition-colors`}
                      >
                        {l.label}
                        <ArrowRight size={13} />
                      </Link>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.section>

      <motion.section
        variants={sectionVariant}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        className="bg-slate-900 px-5 py-20 sm:px-8 lg:px-12"
      >
        <div className="mx-auto max-w-5xl">
          <div className="mb-10 text-center">
            <p className="mb-2 text-sm font-semibold uppercase tracking-widest text-emerald-400">Simple process</p>
            <h2 className="text-3xl font-bold text-white sm:text-4xl">From signup to measurable impact</h2>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {STEPS.map((s, idx) => (
              <motion.div
                key={s.step}
                custom={idx}
                variants={cardVariant}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.35 }}
                className="rounded-2xl border border-white/10 bg-white/3 p-6"
              >
                <span className="mb-3 inline-flex rounded-lg bg-emerald-500/15 px-3 py-1 text-xs font-bold tracking-wider text-emerald-300">
                  STEP {s.step}
                </span>
                <h3 className="mb-2 text-lg font-semibold text-white">{s.title}</h3>
                <p className="text-sm leading-relaxed text-slate-300">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      <motion.section
        variants={sectionVariant}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        className="px-5 py-20 sm:px-8 lg:px-12"
      >
        <div className="mx-auto max-w-6xl">
          <div className="mb-12 text-center">
            <p className="mb-2 text-sm font-semibold uppercase tracking-widest text-emerald-600">Platform capabilities</p>
            <h2 className="text-3xl font-bold text-slate-900 sm:text-4xl">Professional tooling without complexity</h2>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map((f, idx) => (
              <motion.div
                key={f.title}
                custom={idx}
                variants={cardVariant}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.35 }}
                className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
              >
                <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 transition-colors group-hover:bg-emerald-100">
                  <f.icon size={19} />
                </div>
                <h3 className="mb-1.5 text-sm font-bold text-slate-900">{f.title}</h3>
                <p className="text-xs leading-relaxed text-slate-500">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      <motion.section
        variants={sectionVariant}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        className="px-5 pb-24 sm:px-8 lg:px-12"
      >
        <div className="mx-auto max-w-4xl rounded-3xl bg-linear-to-r from-emerald-700 via-emerald-600 to-teal-600 p-10 text-center shadow-xl">
          <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-white/20">
            <Recycle size={28} className="text-white" />
          </div>
          <h2 className="mb-4 text-3xl font-bold text-white sm:text-4xl">Ready to elevate waste operations?</h2>
          <p className="mx-auto mb-8 max-w-2xl text-base leading-relaxed text-emerald-50">
            Join WasteZero and coordinate opportunities, volunteers, and pickups from one professional system designed for scale.
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 rounded-xl bg-white px-8 py-3.5 text-base font-semibold text-emerald-800 transition-all duration-200 hover:bg-emerald-50"
            >
              Create Free Account
              <ArrowRight size={16} />
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 rounded-xl border border-white/30 bg-white/10 px-8 py-3.5 text-base font-semibold text-white transition-all duration-200 hover:bg-white/20"
            >
              Sign In
            </Link>
          </div>
        </div>
      </motion.section>

    </div>
  );
}
